/**
 * Trustpilot Review Widget Tab - Media slider (All/Images/Videos) and modal.
 * jQuery used only for Slick carousel (theme loads it; Slick has no vanilla API).
 */

class TrustpilotReviewWidgetTab extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.currentItems = [];
    this.modal = null;
    this.sliderElement = null;
    this.attachmentsLoaded = false;

    /** ADA Compliance: Configurable announcement messages */
    this.announcements = {
      tabChanged: (tabName) => `Showing ${tabName}.`,
    };
  }

  /** jQuery wrapper for Slick API only (single place we touch jQuery). */
  getSliderJquery() {
    return window.jQuery && this.sliderElement ? window.jQuery(this.sliderElement) : null;
  }

  connectedCallback() {
    /** Wait for jQuery + Slick (theme-loaded) then init. */
    const checkDependencies = () => {
      if (window.jQuery && window.jQuery.fn.slick) {
        this.init();
      } else {
        setTimeout(checkDependencies, 100);
      }
    };

    checkDependencies();
  }

  /** Resolve slider and modal refs; if slider visible init content, else wait for tab open. */
  init() {
    setTimeout(() => {
      this.sliderElement = this.querySelector('[data-trustpilot-slider]');
      const parentWidget = this.closest('trustpilot-review-widget');
      this.modal = parentWidget
        ? parentWidget.querySelector('[data-tp-modal]')
        : document.querySelector('[data-tp-modal]');

      if (!this.sliderElement) return;

      if (this.sliderElement.classList.contains('slick-initialized')) {
        this.setupFocus();
        this.checkArrows();
        return;
      }

      /** Check if review widget tab element is visible */
      if (this.offsetParent !== null) {
        /** Visible review tab - initialize review widget tab content now */
        this.initializeContent();
      } else {
        /** Hidden review tab - wait for review widget tab to be clicked */
        this.waitForTabOpen();
      }
    }, 0);
  }

  /** When tab is hidden, attach one-time listener to desktop tab or mobile accordion to run init on open. */
  waitForTabOpen() {
    const tabPanel = this.closest('[role="tabpanel"]');
    const accordionSection = this.closest('.prod-tabcordion--entry, .plus-icon-accordion');

    let tab = null;
    let mobileAccordion = null;

    /** Desktop: tab button with aria-controls */
    if (tabPanel) {
      const panelId = tabPanel.id;
      tab = document.querySelector(`[aria-controls="${panelId}"]`);
    }

    /** Mobile: accordion controller is a sibling/parent element, not inside tabPanel */
    if (accordionSection) {
      mobileAccordion = accordionSection.querySelector('.mobile-accordion-controller');
    }

    const handleClick = () => {
      if (tab) tab.removeEventListener('click', handleClick);
      if (mobileAccordion) mobileAccordion.removeEventListener('click', handleClick);

      setTimeout(() => {
        /** If slick already initialized (by parent widget), just refresh it */
        if (this.sliderElement.classList.contains('slick-initialized')) {
          this.getSliderJquery()?.slick('setPosition');
          this.setupFocus();
          this.checkArrows();
        } else {
          this.initializeContent();
        }
      }, 100);
    };

    if (tab) tab.addEventListener('click', handleClick);
    if (mobileAccordion) mobileAccordion.addEventListener('click', handleClick);

    if (tab || mobileAccordion) return;

    this.initializeContent();
  }

  /** Show or hide the whole section based on whether any media exists. */
  toggleSectionVisibility(hasMedia) {
    const section = this.closest('.trustpilot-widget-tab-section');
    if (!section) return;
    if (hasMedia) {
      section.classList.remove('trustpilot-widget-tab-section--no-media');
      section.removeAttribute('aria-hidden');
    } else {
      section.classList.add('trustpilot-widget-tab-section--no-media');
      section.setAttribute('aria-hidden', 'true');
    }
  }

  /** Load attachments from parent widget, hide skeleton, init tabs + modal + Slick. */
  initializeContent() {
    if (this.sliderElement.classList.contains('slick-initialized')) return;

    this.loadAttachments().then(() => {
      const skeleton = this.querySelector('[data-skeleton-media-items]');
      if (skeleton) skeleton.classList.add('hidden');

      const mediaCount = this.sliderElement.querySelectorAll('[data-trustpilot-item]').length;
      this.toggleSectionVisibility(mediaCount > 0);
      this.initTabs();
      this.syncAriaSelectedState();

      if (mediaCount <= 1) this.setupFocus();

      this.initModalLogic();
      this.revealMediaAndInitSlider(mediaCount);
    });
  }

  /** Slides to show for current viewport (from data-item-desktop / tablet / mobile). */
  getSlidesToShowForViewport() {
    const w = typeof window !== 'undefined' ? window.innerWidth : 9999;
    const desktop = parseInt(this.getAttribute('data-item-desktop')) || 6;
    const tablet = parseInt(this.getAttribute('data-item-tablet')) || 4;
    const mobile = parseInt(this.getAttribute('data-item-mobile')) || 3;
    if (w > 1024) return desktop;
    if (w > 768) return tablet;
    return mobile;
  }

  /** True when item count exceeds visible slots for current device, so slider is needed. */
  shouldInitSlider(mediaCount) {
    return mediaCount > this.getSlidesToShowForViewport();
  }

  /** Show slider and init Slick only when items exceed device limit (e.g. 5 images + desktop 6 = no slider). */
  revealMediaAndInitSlider(mediaCount) {
    if (this.closest('[data-review-card]')) return;
    const widget = this.closest('trustpilot-review-widget');
    if (widget && typeof widget.toggleMediaSkeleton === 'function') {
      widget.toggleMediaSkeleton(false);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.shouldInitSlider(mediaCount)) {
          this.initSlider();
        }
      });
    });

    /** On resize: init slider if we didn’t before but now items exceed device limit (e.g. desktop 5→tablet 5). */
    if (this._resizeInitAttached) return;
    this._resizeInitAttached = true;
    let resizeInitT = null;
    window.addEventListener('resize', () => {
      if (resizeInitT) clearTimeout(resizeInitT);
      resizeInitT = setTimeout(() => {
        if (this.sliderElement?.classList.contains('slick-initialized')) return;
        const count = this.sliderElement?.querySelectorAll('[data-trustpilot-item]').length ?? 0;
        if (count > 0 && this.shouldInitSlider(count)) {
          this.initSlider();
        }
      }, 150);
    });
  }

  /** Called by widget after filter/sort: unslick, reload attachments, re-init tabs and Slick. */
  async refreshContent() {
    if (this.sliderElement?.classList.contains('slick-initialized')) {
      this.getSliderJquery()?.slick('unslick');
    }

    this.attachmentsLoaded = false;
    await this.loadAttachments();

    const skeleton = this.querySelector('[data-skeleton-media-items]');
    if (skeleton) skeleton.classList.add('hidden');

    const mediaCount = this.sliderElement.querySelectorAll('[data-trustpilot-item]').length;
    this.toggleSectionVisibility(mediaCount > 0);
    if (mediaCount <= 1) this.setupFocus();

    this.initModalLogic();

    const section = this.closest('.trustpilot-widget-tab-section');
    if (section) {
      const emptyImages = section.querySelector('[data-tp-media-empty-state="images"]');
      const emptyVideos = section.querySelector('[data-tp-media-empty-state="videos"]');
      this.sliderElement.classList.remove('hidden');
      this.sliderElement.removeAttribute('aria-hidden');
      if (emptyImages) emptyImages.classList.add('hidden');
      if (emptyVideos) emptyVideos.classList.add('hidden');
    }

    const widget = this.closest('trustpilot-review-widget');
    if (widget && typeof widget.toggleMediaSkeleton === 'function') {
      widget.toggleMediaSkeleton(false);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.shouldInitSlider(mediaCount)) {
          this.initSlider();
        }
        this.initTabs();
        this.syncAriaSelectedState();
      });
    });
  }

  /** Init Slick when item count exceeds device limit (data-item-desktop / tablet / mobile). */
  initSlider() {
    if (this.sliderElement.classList.contains('slick-initialized')) return;
    const itemCount = this.sliderElement.querySelectorAll('[data-trustpilot-item]').length;
    if (!this.shouldInitSlider(itemCount)) return;

    if (this.sliderElement.offsetWidth <= 0) {
      requestAnimationFrame(() => this.initSlider());
      return;
    }

    const slidesDesktop = parseInt(this.getAttribute('data-item-desktop')) || 6;
    const slidesTablet = parseInt(this.getAttribute('data-item-tablet')) || 4;
    const slidesMobile = parseInt(this.getAttribute('data-item-mobile')) || 3;
    const w = typeof window !== 'undefined' ? window.innerWidth : 9999;
    const initialSlidesToShow = w > 1024 ? slidesDesktop : w > 768 ? slidesTablet : slidesMobile;
    const initialSlidesToScroll = Math.min(initialSlidesToShow, 3);

    const settings = {
      slidesToShow: initialSlidesToShow,
      slidesToScroll: initialSlidesToScroll,
      infinite: false,
      dots: false,
      arrows: true,
      accessibility: true,
      focusOnSelect: true,
      prevArrow: '<button type="button" class="slick-prev" aria-label="Previous review media"><span aria-hidden="true">&#10094;</span></button>',
      nextArrow: '<button type="button" class="slick-next" aria-label="Next review media"><span aria-hidden="true">&#10095;</span></button>',
      responsive: [
        { breakpoint: 1024, settings: { slidesToShow: slidesTablet, slidesToScroll: 1 } },
        { breakpoint: 768, settings: { slidesToShow: slidesMobile, slidesToScroll: 1 } },
      ],
    };

    const $slider = this.getSliderJquery();
    if (!$slider || !window.jQuery?.fn.slick) return;

    if ($slider.hasClass('slick-initialized')) $slider.slick('unslick');
    $slider.on('init', (e, slick) => {
      this.checkArrows(slick);
      this.setupFocus();
    });
    $slider.on('afterChange', () => this.setupFocus());

    $slider.slick(settings);
  }

  /** Wire All/Images/Videos tabs: filter Slick slides and show empty state when no media for tab. */
  initTabs() {
    const section = this.closest('.trustpilot-widget-tab-section');
    if (!section) return;

    const tabs = Array.from(section.querySelectorAll('[data-trustpilot-tab]'));
    const emptyImages = section.querySelector('[data-tp-media-empty-state="images"]');
    const emptyVideos = section.querySelector('[data-tp-media-empty-state="videos"]');
    const slickInitialized = this.sliderElement.classList.contains('slick-initialized');

    const showSlider = () => {
      this.sliderElement.classList.remove('hidden');
      this.sliderElement.removeAttribute('aria-hidden');

      if (emptyImages) emptyImages.classList.add('hidden');
      if (emptyVideos) emptyVideos.classList.add('hidden');
    };

    const scheduleSlickSetPosition = () => {
      if (!slickInitialized || !this.getSliderJquery()) return;
      if (!this.sliderElement.classList.contains('slick-initialized')) return;
      this.getSliderJquery()?.slick('setPosition');
    };

    const showMediaEmptyState = (type) => {
      this.sliderElement.classList.add('hidden');
      this.sliderElement.setAttribute('aria-hidden', 'true');
      if (emptyImages) emptyImages.classList.toggle('hidden', type !== 'images');
      if (emptyVideos) emptyVideos.classList.toggle('hidden', type !== 'videos');
    };

    /** Set initial tabindex and aria-selected: only active tab focusable. */
    tabs.forEach((t) => {
      const isActive = t.classList.contains('active');
      t.setAttribute('tabindex', isActive ? '0' : '-1');
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    const getMediaTypeFromSlide = (element) => {
      if (!element) return '';
      const inner =
        (element.querySelector && element.querySelector('[data-tp-item-inner]')) ||
        (element.hasAttribute && element.hasAttribute('data-tp-item-inner') ? element : null);
      const node = inner || element;
      return (node.getAttribute && node.getAttribute('data-media-type')) || '';
    };

    /** Show slider or empty state for tab (all | images | videos). */
    const applyTabState = (target) => {
      const allItems = this.sliderElement.querySelectorAll('[data-trustpilot-item]');

      const imageCount = Array.from(allItems).filter((el) => {
        const itemInner = el.querySelector('[data-tp-item-inner]');
        return itemInner && itemInner.getAttribute('data-media-type') === 'image';
      }).length;

      const videoCount = Array.from(allItems).filter((el) => {
        const itemInner = el.querySelector('[data-tp-item-inner]');
        return itemInner && itemInner.getAttribute('data-media-type') === 'video';
      }).length;

      if (target === 'all') {
        showSlider();
        if (slickInitialized) {
          this.getSliderJquery()?.slick('slickUnfilter');
          scheduleSlickSetPosition();
        }
      } else if (target === 'images') {
        if (imageCount > 0) {
          showSlider();
          if (slickInitialized) {
            const $slider = this.getSliderJquery();
            if ($slider) {
              $slider.slick('slickUnfilter');
              $slider.slick('slickFilter', function (idx, element) {
                const slideEl = element || this;

                return getMediaTypeFromSlide(slideEl) === 'image';
              });
            }
            scheduleSlickSetPosition();
          }
        } else {
          if (slickInitialized) this.getSliderJquery()?.slick('slickUnfilter');
          showMediaEmptyState('images');
        }
      } else if (target === 'videos') {
        if (videoCount > 0) {
          showSlider();
          if (slickInitialized) {
            const $slider = this.getSliderJquery();
            if ($slider) {
              $slider.slick('slickUnfilter');
              $slider.slick('slickFilter', function (idx, element) {
                const slideEl = element || this;

                return getMediaTypeFromSlide(slideEl) === 'video';
              });
            }
            scheduleSlickSetPosition();
          }
        } else {
          if (slickInitialized) this.getSliderJquery()?.slick('slickUnfilter');
          showMediaEmptyState('videos');
        }
      }
    };

    tabs.forEach((t) => t.setAttribute('tabindex', '0'));

    tabs.forEach((tab, index) => {
      const triggerTab = () => {
        const target = tab.getAttribute('data-tab');
        const panel = section.querySelector('#trustpilot-media-panel');

        tabs.forEach((t) => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
          t.setAttribute('tabindex', '-1');
        });

        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');

        if (panel) {
          const tabId = tab.id || `trustpilot-tab-${target}`;
          panel.setAttribute('aria-labelledby', tabId);
        }

        if (section) section.setAttribute('data-active-media-tab', target);
        applyTabState(target);
        this.syncAriaSelectedState();

        setTimeout(() => {
          const tabName =
            target === 'all' ? 'All media' : target === 'images' ? 'Images' : 'Videos';
          this.announceTabChange(tabName);
        }, 100);

        this.checkArrows();
        this.setupFocus();
      };

      tab.addEventListener('click', () => {
        triggerTab();
        tab.focus();
      });

      tab.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          triggerTab();
          tab.focus();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          const nextTab = tabs[(index + 1) % tabs.length];
          nextTab.setAttribute('tabindex', '0');
          nextTab.focus();
          tab.setAttribute('tabindex', '-1');
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prevTab = tabs[(index - 1 + tabs.length) % tabs.length];
          prevTab.setAttribute('tabindex', '0');
          prevTab.focus();
          tab.setAttribute('tabindex', '-1');
        } else if (e.key === 'Tab' && !e.shiftKey && tab.classList.contains('active')) {
          e.preventDefault();
          this.focusActivePanel();
        }
      });
    });

    const activeTab = tabs.find((t) => t.classList.contains('active')) || tabs[0];
    const savedTab = section.getAttribute('data-active-media-tab');
    const target = savedTab || activeTab.getAttribute('data-tab') || 'all';

    if (target !== (activeTab.getAttribute('data-tab') || 'all')) {
      const tabToActivate = tabs.find((t) => t.getAttribute('data-tab') === target) || tabs[0];
      tabs.forEach((t) => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
        t.setAttribute('tabindex', '-1');
      });

      tabToActivate.classList.add('active');
      tabToActivate.setAttribute('aria-selected', 'true');
      tabToActivate.setAttribute('tabindex', '0');
      section.setAttribute('data-active-media-tab', target);
    }
    applyTabState(target);
  }

  initModalLogic() {
    if (window.trustpilotReviewModal && !window.trustpilotReviewModal.isInitialized) {
      window.trustpilotReviewModal.init();
    }

    this.sliderElement.addEventListener('click', (e) => {
      const itemInner = e.target.closest('[data-tp-item-inner]');
      if (itemInner) this.openModal(e);
    });

    this.sliderElement.addEventListener('keydown', (e) => {
      const itemInner = e.target.closest('[data-tp-item-inner]');
      if (itemInner && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        this.openModal(e);
      }
    });
  }

  /** Get parent widget, wait for reviews if needed, then call buildAllAttachmentsForTab to fill slider. */
  async loadAttachments() {
    if (this.attachmentsLoaded) return;

    const isInReviewCard = this.closest('[data-review-card]') !== null;
    if (
      isInReviewCard &&
      this.sliderElement.querySelectorAll('[data-trustpilot-item]').length > 0
    ) {
      this.attachmentsLoaded = true;
      return;
    }

    const parent = this.closest('trustpilot-review-widget');

    if (!parent) {
      const loadingEl = this.sliderElement.querySelector('[data-loading-attachments]');
      if (loadingEl) loadingEl.remove();

      this.sliderElement.innerHTML = '<div class="no-attachments">No media available</div>';
      this.attachmentsLoaded = true;
      return;
    }

    if (!parent.reviews || parent.reviews.length === 0) {
      await new Promise((resolve) => {
        if (parent.reviews && parent.reviews.length > 0) {
          resolve();
        } else {
          const handler = () => {
            parent.removeEventListener('reviews-loaded', handler);
            resolve();
          };

          parent.addEventListener('reviews-loaded', handler);

          setTimeout(() => {
            parent.removeEventListener('reviews-loaded', handler);
            resolve();
          }, 5000);
        }
      });
    }

    if (parent.buildAllAttachmentsForTab) {
      const loadingEl = this.sliderElement.querySelector('[data-loading-attachments]');
      if (loadingEl) loadingEl.remove();

      parent.buildAllAttachmentsForTab(this.sliderElement);
      this.attachmentsLoaded = true;
    } else {
      const loadingEl = this.sliderElement.querySelector('[data-loading-attachments]');
      if (loadingEl) loadingEl.remove();

      this.sliderElement.innerHTML = '<div class="no-attachments">No media available</div>';
      this.attachmentsLoaded = true;
    }
  }

  openModal(event) {
    const clickedItem = event.target.closest('[data-tp-item-inner]');
    if (!clickedItem || !window.trustpilotReviewModal) return;

    let mediaItems = [];
    let currentIndex = 0;

    if (this.sliderElement.classList.contains('slick-initialized')) {
      mediaItems = Array.from(
        this.sliderElement.querySelectorAll(
          '[data-tp-item-inner]:not(.slick-cloned [data-tp-item-inner])'
        )
      );
      const slide = clickedItem.closest('.slick-slide');

      if (slide) {
        const slickIndex = parseInt(
          slide.getAttribute('data-slick-index') || slide.dataset.slickIndex || '0'
        );
        const slickInstance = this.getSliderJquery()?.slick('getSlick');
        const slideCount = slickInstance?.slideCount;

        currentIndex =
          slideCount != null
            ? ((slickIndex % slideCount) + slideCount) % slideCount
            : mediaItems.indexOf(clickedItem);
      } else {
        currentIndex = mediaItems.indexOf(clickedItem);
      }
    } else {
      mediaItems = Array.from(this.sliderElement.querySelectorAll('[data-tp-item-inner]'));
      currentIndex = mediaItems.indexOf(clickedItem);
    }

    if (currentIndex < 0) return;
    window.trustpilotReviewModal.open(this, currentIndex, mediaItems);
  }

  closeModal() {
    if (window.trustpilotReviewModal) {
      window.trustpilotReviewModal.close();
    }
  }

  nextMedia() {
    if (!this.currentItems.length) return;
    this.currentIndex = (this.currentIndex + 1) % this.currentItems.length;

    this.updateModalContent();

    if (window.trustpilotReviewModal && this.currentItems[this.currentIndex]) {
      const mediaType = this.currentItems[this.currentIndex].getAttribute('data-media-type') || 'image';
      const total = this.currentItems.length;

      window.trustpilotReviewModal.announce(
        window.trustpilotReviewModal.announcements.nextMedia(
          this.currentIndex + 1,
          total,
          mediaType
        )
      );
    }
  }

  prevMedia() {
    if (!this.currentItems.length) return;
    this.currentIndex =
      (this.currentIndex - 1 + this.currentItems.length) % this.currentItems.length;

    this.updateModalContent();

    if (window.trustpilotReviewModal && this.currentItems[this.currentIndex]) {
      const mediaType = this.currentItems[this.currentIndex].getAttribute('data-media-type') || 'image';
      const total = this.currentItems.length;

      window.trustpilotReviewModal.announce(
        window.trustpilotReviewModal.announcements.prevMedia(
          this.currentIndex + 1,
          total,
          mediaType
        )
      );
    }
  }

  updateModalContent() {
    if (
      !this.currentItems?.length ||
      this.currentIndex < 0 ||
      this.currentIndex >= this.currentItems.length
    )
      return;

    if (!this.modal) {
      const parentWidget = this.closest('trustpilot-review-widget');
      this.modal = parentWidget
        ? parentWidget.querySelector('[data-tp-modal]')
        : document.querySelector('[data-tp-modal]');
    }
    if (!this.modal) return;

    const domElement = this.currentItems[this.currentIndex];
    if (!domElement) return;

    const type = domElement.getAttribute('data-media-type') || '';
    const username = domElement.getAttribute('data-user') || 'Anonymous';

    const reviewText = domElement.getAttribute('data-review') || '';
    const reviewTitle = domElement.getAttribute('data-title') || '';
    const reviewTime = domElement.getAttribute('data-time') || '';
    const reviewRating = parseInt(domElement.getAttribute('data-rating') || '5', 10);

    const imgUrl = domElement.getAttribute('data-image') || '';
    const videoUrl = domElement.getAttribute('data-video') || '';

    const mediaContainer = this.modal.querySelector('[data-tp-modal-media-container]');
    if (!mediaContainer) return;

    const existingMedia = mediaContainer.querySelector('img, video');
    if (existingMedia) existingMedia.remove();

    if (type === 'video') {
      const escapedVideoUrl = videoUrl.replace(/"/g, '&quot;');
      const videoHtml = `<video controls autoplay tabindex="0" style="width:100%; height:100%; object-fit:contain; background:#000;"><source src="${escapedVideoUrl}" type="video/mp4"></video>`;
      mediaContainer.insertAdjacentHTML('afterbegin', videoHtml);

      /** Set tabindex to allow keyboard navigation to video controls */
      const videoElement = mediaContainer.querySelector('video');
      if (videoElement) {
        videoElement.setAttribute('tabindex', '0');
      }
    } else {
      const escapedImgUrl = imgUrl.replace(/"/g, '&quot;');
      const imgHtml = `<img src="${escapedImgUrl}" style="width:100%; height:100%; object-fit:contain;" alt="Review image">`;
      mediaContainer.insertAdjacentHTML('afterbegin', imgHtml);
    }

    const prevBtn = this.modal.querySelector('[data-tp-modal-prev-btn]');
    const nextBtn = this.modal.querySelector('[data-tp-modal-next-btn]');
    const shouldHideNav = this.currentItems.length <= 1;

    if (prevBtn) prevBtn.style.display = shouldHideNav ? 'none' : '';
    if (nextBtn) nextBtn.style.display = shouldHideNav ? 'none' : '';

    const usernameEl = this.modal.querySelector('[data-tp-modal-username]');
    const reviewTextEl = this.modal.querySelector('[data-tp-modal-review-text]');
    const reviewTitleEl = this.modal.querySelector('[data-tp-modal-review-title]');
    const reviewTimeEl = this.modal.querySelector('[data-tp-modal-review-time]');

    if (usernameEl && username) usernameEl.textContent = username;
    if (reviewTextEl && reviewText) reviewTextEl.textContent = reviewText;
    if (reviewTitleEl && reviewTitle) reviewTitleEl.textContent = reviewTitle;
    if (reviewTimeEl && reviewTime) reviewTimeEl.textContent = reviewTime;

    const avatar = this.modal.querySelector('[data-tp-modal-avatar]');
    if (avatar && username) {
      avatar.textContent = username.charAt(0).toUpperCase();
    }

    const ratingElement = this.modal.querySelector('[data-tp-modal-rating]');
    if (ratingElement) {
      const rating = Math.max(0, Math.min(5, Math.floor(reviewRating || 0)));
      const filledStars = '★'.repeat(rating);
      const emptyStars = '☆'.repeat(5 - rating);
      ratingElement.textContent = filledStars + emptyStars;
      ratingElement.setAttribute('aria-label', `${reviewRating} out of 5 stars`);
    }
  }

  setupFocus() {
    setTimeout(() => {
      const allItems = Array.from(this.sliderElement.querySelectorAll('[data-tp-item-inner]'));

      // First, set all items to -1
      allItems.forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });

      // Then, set visible items to 0, but only the first one should be focusable initially
      let firstVisibleFound = false;
      allItems.forEach((el) => {
        const slide = el.closest('.slick-slide');
        if (!slide) return;

        const isCloned = slide.classList.contains('slick-cloned');
        const ariaHidden = slide.getAttribute('aria-hidden');
        const isActive = slide.classList.contains('slick-active');
        const isVisible = !isCloned && isActive && ariaHidden !== 'true';

        if (isVisible && !firstVisibleFound) {
          el.setAttribute('tabindex', '0');
          firstVisibleFound = true;
        } else if (isVisible) {
          // Other visible items can also be focusable for keyboard navigation
          el.setAttribute('tabindex', '0');
        }
      });

      const items = allItems.filter((el) => {
        const slide = el.closest('.slick-slide');
        return (
          slide &&
          !slide.classList.contains('slick-cloned') &&
          slide.classList.contains('slick-active') &&
          slide.getAttribute('aria-hidden') !== 'true'
        );
      });

      items.forEach((el) => {
        if (!el.dataset.focusBound) {
          el.addEventListener('focus', () => {
            if (this.sliderElement.classList.contains('slick-initialized')) {
              const slide = el.closest('.slick-slide');
              if (slide) {
                const slideIndex = parseInt(
                  slide.getAttribute('data-slick-index') || slide.dataset.slickIndex || '0'
                );
                this.getSliderJquery()?.slick('slickGoTo', slideIndex);
              }
            }
          });

          el.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              e.preventDefault();
              const currentVisibleItems = Array.from(
                this.sliderElement.querySelectorAll('[data-tp-item-inner]')
              ).filter((item) => {
                const slide = item.closest('.slick-slide');
                return (
                  slide &&
                  !slide.classList.contains('slick-cloned') &&
                  slide.getAttribute('aria-hidden') !== 'true'
                );
              });

              const currentIdx = currentVisibleItems.indexOf(el);
              if (currentIdx === -1) return;

              const nextIdx =
                e.key === 'ArrowRight'
                  ? (currentIdx + 1) % currentVisibleItems.length
                  : (currentIdx - 1 + currentVisibleItems.length) % currentVisibleItems.length;
              currentVisibleItems[nextIdx].focus();
            }
          });
          el.dataset.focusBound = 'true';
        }
      });
    }, 50);
  }

  checkArrows(slick) {
    if (!this.sliderElement.classList.contains('slick-initialized')) return;

    if (!slick) {
      slick = this.getSliderJquery()?.slick('getSlick');
    }

    const arrows = this.sliderElement.querySelectorAll('.slick-arrow');
    const shouldHide = slick && slick.slideCount <= slick.options.slidesToShow;

    arrows.forEach((arrow) => {
      arrow.classList.toggle('tp-arrows-hide', shouldHide);
    });
  }

  /** Sync aria-selected with active class so screen readers match visual state. */
  syncAriaSelectedState() {
    const section = this.closest('.trustpilot-widget-tab-section');
    if (!section) return;
    
    const tabs = Array.from(section.querySelectorAll('[data-trustpilot-tab]'));
    tabs.forEach((tab) => {
      const isActive = tab.classList.contains('active');
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  announceTabChange(tabName) {
    const liveRegion = document.getElementById('trustpilot-aria-live');
    if (!liveRegion) return;

    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.textContent = '';

    setTimeout(() => {
      liveRegion.textContent = this.announcements.tabChanged(tabName);
      
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 1500);
    }, 50);
  }

  /** When Tab is pressed on active tab, move focus into the media panel (first focusable item or panel). */
  focusActivePanel() {
    const section = this.closest('.trustpilot-widget-tab-section');
    if (!section) return;

    const panel = section.querySelector('#trustpilot-media-panel');
    if (!panel) return;

    setTimeout(() => {
      if (panel.classList.contains('hidden')) {
        const emptyStates = section.querySelectorAll('[data-tp-media-empty-state]');
        for (const emptyState of emptyStates) {
          if (!emptyState.classList.contains('hidden')) {
            if (!emptyState.hasAttribute('tabindex')) {
              emptyState.setAttribute('tabindex', '0');
            }
            emptyState.focus();
            return;
          }
        }
        return;
      }

      /** Panel is visible, try to focus first focusable element inside the panel */
      /** Priority: first visible media item > slider arrows > panel itself */
      const focusableSelectors = [
        '[data-tp-item-inner][tabindex="0"]',
        '[data-tp-item-inner]',
        '.slick-arrow:not(.tp-arrows-hide):not(.slick-disabled)',
      ];

      for (const selector of focusableSelectors) {
        const focusable = panel.querySelector(selector);
        if (focusable && focusable.offsetParent !== null) {
          focusable.focus();
          return;
        }
      }

      /** If no focusable element found, focus the panel itself */
      if (panel.offsetParent !== null) {
        panel.setAttribute('tabindex', '-1');
        panel.focus();
      }
    }, 150);
  }
}

if (!customElements.get('trustpilot-review-widget-tab')) {
  customElements.define('trustpilot-review-widget-tab', TrustpilotReviewWidgetTab);
}
